import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import "./QrCodeScanner.css";
import config from "./config";

const QrCodeScanner = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [error, setError] = useState(null);
  const [hasScanned, setHasScanned] = useState(false);
  const [currentTime, setCurrentTime] = useState(
      new Date().toLocaleTimeString("en-GB")
  );
  const [loading, setLoading] = useState(true); // Track API loading status
  const [permissionGranted, setPermissionGranted] = useState(false); // Track camera permission

  const [availableCameras, setAvailableCameras] = useState([]);

  const plate = searchParams.get("plate");
  const guid = searchParams.get("guid"); // Ensure 'guid' is retrieved

  // Reference to the scanner instance
  const videoRef = useRef(null);
  const observerRef = useRef(null); // Reference for MutationObserver

  // Helper function to detect if the device is mobile
  const isMobileDevice = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await fetch(`${config.app.serverUrl}/sites`);
        if (!response.ok) {
          throw new Error("תקלה בטעינת האתרים");
        }
        const data = await response.json();
        const site = data.sites.find(
            (site) => site.lane_guid === searchParams.get("guid")
        );

        if (!site || !site.active) {
          setError("עמדה לא פעילה. חזרה לעמוד הראשי...");
          setTimeout(() => navigate("/", { replace: true }), 3000);
          return;
        }

        setLoading(false); // API resolved successfully
      } catch (error) {
        console.error("Error fetching sites:", error);
        setError("תקלה בטעינת האתרים. אנא נסו שנית.");
        setLoading(false);
      }
    };

    fetchSites();
  }, [navigate, searchParams]);

  useEffect(() => {
    if (loading || error) {
      return; // Prevent scanner from starting until API resolves
    }

    if (!guid || !plate) {
      setError("פרטים חסרים. חזרה לדף הראשי בעוד 3 שניות...");
      setTimeout(() => navigate(`/enter-car?guid=${guid}`, { replace: true }), 3000);
      return;
    }

    const html5QrCode = new Html5Qrcode("qr-reader");

    const startScanner = async () => {
      try {
        const cameras = await Html5Qrcode.getCameras();

        console.log("Available video input devices:", cameras);

        if (!cameras || cameras.length === 0) {
          throw new Error("לא נמצאו מצלמות זמינות.");
        }

        let selectedDevice = null;

        if (isMobileDevice()) {
          // **Mobile Device Logic: Enhanced Camera Selection**
          // Filter for environment-facing cameras with comprehensive labels
          const environmentDevices = cameras
              .filter((device) =>
                  (
                      device.label.toLowerCase().includes("back") ||
                      device.label.toLowerCase().includes("rear") ||
                      device.label.toLowerCase().includes("environment") ||
                      device.label.toLowerCase().includes("back-facing") ||
                      device.label.toLowerCase().includes("rear-facing") ||
                      device.label.toLowerCase().includes("telephoto") ||
                      device.label.toLowerCase().includes("wide") ||
                      device.label.toLowerCase().includes("ultra-wide") ||
                      device.label.toLowerCase().includes("main") ||
                      device.label.toLowerCase().includes("camera 0")
                  ) &&
                  !(
                      device.label.toLowerCase().includes("front") ||
                      device.label.toLowerCase().includes("selfie") ||
                      device.label.toLowerCase().includes("user") ||
                      device.label.toLowerCase().includes("face") ||
                      device.label.toLowerCase().includes("camera 1")
                  )
              )
              .sort((a, b) => {
                // Prioritize telephoto first, then ultra-wide, then wide
                const priorityOrder = ["ultra-wide", "telephoto", "wide"];
                const aPriority = priorityOrder.findIndex(type => a.label.toLowerCase().includes(type));
                const bPriority = priorityOrder.findIndex(type => b.label.toLowerCase().includes(type));

                if (aPriority === -1 && bPriority === -1) return 0;
                if (aPriority === -1) return 1;
                if (bPriority === -1) return -1;
                return aPriority - bPriority;
              });

          console.log("Environment-facing devices:", environmentDevices);

          setAvailableCameras(environmentDevices);

          if (environmentDevices.length === 0) {
            console.warn("No environment-facing cameras detected. Falling back to default camera.");
            // Fallback to all devices if no environment-facing cameras are found
            selectedDevice = cameras[0];
          } else {
            // Select the first environment-facing camera (highest priority)
            selectedDevice = environmentDevices[0];
            console.log("Selected environment-facing device:", selectedDevice);
          }
        } else {
          // **Non-Mobile Device Logic: Default Camera Selection**
          // Select the first available camera
          selectedDevice = cameras[0];
          console.log("Selected non-mobile device:", selectedDevice);
        }

        // Define QR Code scanner configuration with video constraints
        const scannerConfig = {
          fps: 10, // Frames per second for scanning
          qrbox: 250, // Define scanning box size
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
          videoConstraints: {
            width: 1920,
            height: 1080,
            facingMode: "environment",
          },
        };

        // Define QR Code success and error callbacks
        const qrCodeSuccessCallback = (decodedText, decodedResult) => {
          if (!hasScanned) {
            setHasScanned(true);
            console.log("Code Scanned:", decodedText);
            console.log("Plate:", plate);
            console.log("GUID:", guid);
            html5QrCode.stop().then(() => {
              navigate("/completion", {
                state: { code: decodedText, plate, deviceguid: guid, withPayment: location.state?.withPayment,withQr:location.state?.withQr, paymentCompany: location.state?.paymentCompany, siteName: location.state?.siteName  },
              });
              videoRef.current = null; // Nullify to prevent double stopping
            }).catch((err) => {
              console.error("Error stopping the QR code scanner:", err);
            });
          }
        };

        const qrCodeErrorCallback = (errorMessage) => {
          // Optionally handle scan errors (e.g., log them)
          console.warn(`QR Code no match: ${errorMessage}`);
        };

        // Start scanning
        await html5QrCode.start(
            { deviceId: { exact: selectedDevice.id } },
            scannerConfig,
            qrCodeSuccessCallback,
            qrCodeErrorCallback
        );

        // Assign the scanner instance to videoRef
        videoRef.current = html5QrCode;

        // **Set Up MutationObserver to Detect Video Element**
        observerRef.current = new MutationObserver((mutationsList, observer) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'childList') {
              for (const node of mutation.addedNodes) {
                if (node.tagName === 'VIDEO') {
                  console.log("Video element detected via MutationObserver.");

                  const videoElement = node;
                  const stream = videoElement.srcObject;
                  if (stream) {
                    const [track] = stream.getVideoTracks();
                    if (track && track.getCapabilities()) {
                      const capabilities = track.getCapabilities();
                      if (capabilities.zoom) {
                        track.applyConstraints({
                          advanced: [{ zoom: 1 }]
                        }).then(() => {
                          console.log("Zoom set to 1 via MutationObserver");
                        }).catch((err) => {
                          console.error("Failed to set zoom via MutationObserver:", err);
                        });
                      } else {
                        console.warn("Zoom control is not supported on this device.");
                      }
                    }
                  }

                  // If you prefer CSS adjustments instead:
                  // videoElement.style.transform = "scale(1)";
                  // videoElement.style.objectFit = "cover";

                  // Disconnect the observer once done
                  observer.disconnect();
                }
              }
            }
          }
        });

        // Start observing the #qr-reader div for child node additions
        const qrReaderDiv = document.getElementById("qr-reader");
        if (qrReaderDiv) {
          observerRef.current.observe(qrReaderDiv, { childList: true });
        }

      } catch (err) {
        console.error("שגיאה במצלמה:", err);
        setError(err.message || "שגיאה במהלך הפעלת הסורק.");
        setLoading(false);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    };

    startScanner();

    // Cleanup function to stop the scanner and disconnect the observer when the component unmounts
    return () => {
      if (videoRef.current) {
        videoRef.current.stop().then(() => {
          console.log("QR Code scanning stopped.");
        }).catch((err) => {
          console.error("Error stopping the QR code scanner:", err);
        });
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, error, navigate, searchParams, hasScanned, guid, plate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString("en-GB"));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
      <div style={{
        width: "100vw",
        height: "100vh",
        backgroundImage: `url(${process.env.PUBLIC_URL}/carImage.png)`,
        backgroundRepeat: "no-repeat",      // ← stop the tiling
        backgroundSize: "cover",          // ← scale so it fills the screen
        backgroundPosition: "center center",  // ← center the wheel
        position: "relative",
        direction: "rtl",
        overflow: "hidden",
      }}>


        <button className="back-button" onClick={() => navigate(-1)}>
          <img
              src={`${process.env.PUBLIC_URL}/arrow.png`}
              alt="חץ אחורה"
              className="arrow-icon"
          />
        </button>
        <span style={{marginTop: "40px", display: "flex", justifyContent: "center", alignItems: "center"}}>
            <svg width="54" height="51" viewBox="0 0 54 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M42.3661 33.2957C42.7059 33.95 42.8947 34.6427 42.8947 35.4125V35.5664C42.8947 38.1066 40.8558 40.2235 38.3262 40.2235H21.0716C18.1644 40.2235 15.7858 42.6482 15.7858 45.6117C15.7858 48.5753 18.1644 51 21.0716 51H38.3262C46.6703 51 53.4286 44.1107 53.4286 35.6049V35.451C53.4286 32.218 52.447 29.216 50.7479 26.7143C48.6336 29.6778 45.7264 31.9871 42.3661 33.3342"
                    fill="#0497CD"/>
                <path
                    d="M5.2937 51C2.38217 51 0 48.6297 0 45.7241V0H38.2659C46.6224 0 53.4286 6.84333 53.4286 15.3306V15.407C53.4286 23.8561 46.6602 30.7376 38.2659 30.7376H23.9729C21.137 30.7376 18.6792 28.5967 18.4901 25.7294C18.3011 22.6327 20.7211 20.0712 23.746 20.0712H38.2281C40.7615 20.0712 42.8034 18.0067 42.8034 15.4453V15.3688C42.8034 12.8073 40.7615 10.7429 38.2281 10.7429H10.5118L10.6252 45.6094C10.6252 48.5532 8.31867 50.9618 5.40714 51C5.40714 51 5.36933 51 5.33151 51"
                    fill="#003C6B"/>
            </svg></span>
        <span style={{
          // position:    "absolute",
          //
          // width: 308,                                   // px
          // height: 18,                                    // px
          // left: "calc(50% - 308px/2 + 1px)",           // use string for calc()
          top: 212,                                   // px automatically
          fontFamily: "Heebo",
          fontStyle: "normal",
          fontWeight: 900,
          fontSize: 23,                                    // px
          lineHeight: "28px",
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
          alignItems: "center",
          textAlign: "center",
          color: "#FFFFFF",
          marginBottom: "50px"
        }}
        >אנא סירקו את קוד ה- QR שברשותכם</span>
        {/*<p className="QR-instruction">אנא סירקו את הקוד שברשותכם</p>*/}
        {error && (
            <span style={{
          fontFamily: "Heebo",
          fontStyle: "normal",
          maxWidth: "80%", // limit width to prevent overflow
          fontWeight: 400,
          fontSize: 18,         // px
          lineHeight: "27px",
          textAlign: "center",
          color: "red",}}>
              {error}
              <br/>
              <span>העמוד יטען מחדש בעוד רגע...</span>
            </span>
        )}
        {!loading && !error && (
            <>
              <div id="qr-reader"></div>
            </>
        )}
        {/*<footer className="footer">*/}
        {/*  <div className="footer-content">*/}
        {/*    <p>*/}
        {/*      מספר רכב נוכחי: <span className="highlight">{plate}</span>*/}
        {/*    </p>*/}
        {/*    <p>*/}
        {/*      שעה נוכחית: <span className="highlight">{currentTime}</span>*/}
        {/*    </p>*/}
        {/*    <p>© BluePT {new Date().getFullYear()}</p>*/}
        {/*  </div>*/}
        {/*</footer>*/}
      </div>
  );
};

export default QrCodeScanner;
